import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageMeta } from '@/components/PageMeta';
import { CityCombobox } from '@/components/geo/CityCombobox';
import { RadiusFilter } from '@/components/geo/RadiusFilter';
import CompetitionCard from '@/components/search-concours/CompetitionCard';
import {
  ALL_STATUSES,
  PAGE_SIZE,
  parseLevel,
  parseOrganizer,
  parsePage,
  parseRadius,
  parseRestriction,
  parseSortBy,
  parseStatusWithDefault,
  parseType,
} from '@/components/search-concours/searchParams';
import { ROUTES } from '@/config/routes';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import { getNearbyCompetitionsApi } from '@/lib/api/geoApi';
import { getTeamsApi } from '@/lib/api/teamsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { CompetitionLevel, CompetitionStatus, CompetitionType } from '@shared/enums/domain.enum';
import type { Commune, Competition } from '@shared/types/entities';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  Locate,
  MapPin,
  Search,
  Shield,
  SlidersHorizontal,
  Star,
  Trophy,
  X,
} from 'lucide-react';

export default function SearchConcours() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePage(searchParams.get('page'));
  const search = searchParams.get('search') ?? '';
  const type = parseType(searchParams.get('type'));
  const level = parseLevel(searchParams.get('level'));
  // Feature 5 — défaut REGISTRATION_OPEN quand aucun `status` en query.
  const status = parseStatusWithDefault(searchParams.get('status'));
  const sortBy = parseSortBy(searchParams.get('sortBy'));
  const radius = parseRadius(searchParams.get('radius'));
  const restriction = parseRestriction(searchParams.get('restriction'));
  // Feature 6 — filtre par organisateur (id de l'organisation via ?organizer=).
  const organizer = parseOrganizer(searchParams.get('organizer'));

  const [searchInput, setSearchInput] = useState(search);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Nearby mode — ephemeral geo state stays local (cannot be meaningfully serialized)
  const [nearbyMode, setNearbyMode] = useState(false);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [userLng, setUserLng] = useState<number | null>(null);
  const [cityInput, setCityInput] = useState('');
  const [cityLabel, setCityLabel] = useState('');
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);

  function updateParams(updates: Record<string, string | undefined>) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined || value === '') next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
  }

  const queryParams = {
    page,
    limit: PAGE_SIZE,
    ...(search && { search }),
    ...(type && { type }),
    ...(level && { level }),
    ...(status && { status }),
    ...(organizer && { organizationId: organizer }),
    ...(restriction !== 'all' && { restriction }),
    sortBy,
    sortOrder: 'asc' as const,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: queryKeys.competitions.list(queryParams),
    queryFn: () => getCompetitionsApi(queryParams),
    placeholderData: keepPreviousData,
    enabled: !nearbyMode,
  });

  const { data: nearbyData, isLoading: isNearbyLoading } = useQuery({
    queryKey: ['competitions', 'nearby', userLat, userLng, radius],
    queryFn: () => getNearbyCompetitionsApi(userLat!, userLng!, radius, undefined, 50),
    enabled: nearbyMode && userLat !== null && userLng !== null,
  });

  // Équipes de l'utilisateur connecté (pour afficher "Déjà inscrit")
  const { data: myTeamsData } = useQuery({
    queryKey: queryKeys.teams.list({ captainId: user?.id }),
    queryFn: () => getTeamsApi({ captainId: user!.id, limit: 100 }),
    enabled: isAuthenticated && !!user,
  });
  const registeredCompetitionIds = useMemo<Set<number>>(() => {
    const ids = new Set<number>();
    for (const t of myTeamsData?.data ?? []) {
      if (t.competitionId && t.registrationStatus !== 'CANCELLED' && t.registrationStatus !== 'REJECTED')
        ids.add(t.competitionId);
    }
    return ids;
  }, [myTeamsData]);

  const competitions = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateParams({ search: value || undefined, page: undefined });
    }, 400);
  }

  function toggleType(v: CompetitionType) {
    updateParams({ type: type === v ? undefined : v, page: undefined });
  }

  function toggleLevel(v: CompetitionLevel) {
    updateParams({ level: level === v ? undefined : v, page: undefined });
  }

  function toggleStatus(v: CompetitionStatus) {
    updateParams({ status: status === v ? undefined : v, page: undefined });
  }

  function exitNearbyMode() {
    setNearbyMode(false);
    setUserLat(null);
    setUserLng(null);
    setCityInput('');
    setCityLabel('');
    setGeoError(null);
  }

  function resetFilters() {
    setSearchInput('');
    exitNearbyMode();
    setSearchParams({}, { replace: true });
  }

  function handleCitySelect(commune: Commune) {
    setUserLat(commune.latitude);
    setUserLng(commune.longitude);
    setCityLabel(commune.name);
    setCityInput('');
    setNearbyMode(true);
    setGeoError(null);
  }

  function handleGeolocation() {
    if (!navigator.geolocation) {
      setGeoError("La géolocalisation n'est pas disponible sur ce navigateur");
      return;
    }
    setGeoLoading(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      pos => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setCityLabel('votre position');
        setCityInput('');
        setNearbyMode(true);
        setGeoLoading(false);
      },
      () => {
        setGeoError("Impossible d'accéder à votre position");
        setGeoLoading(false);
      },
      { timeout: 10000 },
    );
  }

  function getPageNumbers(): (number | '...')[] {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  }

  const displayLoading = nearbyMode ? isNearbyLoading : isLoading;
  const displayTotal = nearbyMode ? (nearbyData?.length ?? 0) : total;

  return (
    <div className='bg-[#F8FAFC] text-slate-900 font-sans'>
      <PageMeta
        title='Rechercher un concours'
        description='Trouvez et inscrivez-vous aux concours de pétanque près de chez vous. Filtrez par type, niveau, statut et localisation.'
      />
      {/* Hero Section */}
      <section className='pt-40 pb-20 px-6 bg-gradient-to-br from-blue-50 via-white to-emerald-50'>
        <div className='max-w-5xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full mb-8'>
            <Trophy className='w-4 h-4 text-blue-600' />
            <span className='text-xs font-bold text-blue-700 uppercase tracking-wider'>
              {isLoading && !nearbyMode ? '…' : `${displayTotal.toLocaleString('fr-FR')} concours disponibles`}
            </span>
          </div>

          <h1 className='text-5xl md:text-6xl font-extrabold text-slate-900 leading-[1.1] mb-8'>
            Trouvez votre prochain
            <br />
            <span className='bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent'>
              concours de pétanque
            </span>
          </h1>

          <p className='text-lg text-slate-500 max-w-2xl mx-auto mb-10 leading-relaxed'>
            Parcourez les concours de pétanque dans toute la France. Inscrivez-vous en ligne en quelques clics et gérez
            vos participations facilement.
          </p>

          {/* Search Widget */}
          <div className='max-w-4xl mx-auto bg-white rounded-3xl shadow-lg border border-slate-200 p-6'>
            <div className='flex items-center bg-slate-50 rounded-xl px-4 py-3 gap-3 mb-4'>
              <Search className='w-5 h-5 text-slate-400 shrink-0' />
              <input
                id={'search-by-competition-or-city'}
                type='text'
                value={searchInput}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder='Nom du concours ou ville...'
                className='w-full text-sm text-slate-700 placeholder:text-slate-400 outline-none bg-transparent'
              />
              {isFetching && !isLoading && !nearbyMode && <span className='text-xs text-slate-400'>…</span>}
            </div>
            <div className='flex flex-wrap items-center gap-3'>
              {[
                { label: 'Doublette', value: CompetitionType.DOUBLE },
                { label: 'Triplette', value: CompetitionType.TRIPLE },
                { label: 'Tête-à-tête', value: CompetitionType.SINGLE },
              ].map(f => (
                <button
                  key={f.value}
                  onClick={() => toggleType(f.value)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                    type === f.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-blue-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <button
                onClick={() => toggleStatus(CompetitionStatus.REGISTRATION_OPEN)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                  status === CompetitionStatus.REGISTRATION_OPEN
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'
                }`}
              >
                Places dispo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Filters + Results */}
      <section className='px-6 py-16'>
        <div className='max-w-7xl mx-auto flex flex-col lg:flex-row gap-8'>
          {/* Sidebar Filters */}
          <aside className='lg:w-1/4 shrink-0'>
            <div className='bg-white rounded-3xl border border-slate-200 p-6 sticky top-28'>
              <div className='flex items-center gap-2 mb-6'>
                <SlidersHorizontal className='w-5 h-5 text-blue-600' />
                <h3 className='font-bold text-lg'>Filtres</h3>
              </div>

              {/* Proximité */}
              <div className='mb-6'>
                <label className='block text-sm font-semibold text-slate-700 mb-3'>Proximité</label>
                {nearbyMode ? (
                  <div className='space-y-3'>
                    <div className='flex items-center gap-2 text-sm bg-blue-50 border border-blue-100 rounded-xl px-3 py-2'>
                      <MapPin className='w-3.5 h-3.5 text-blue-500 shrink-0' />
                      <span className='font-semibold text-blue-700 truncate flex-1'>{cityLabel}</span>
                      <button
                        type='button'
                        onClick={exitNearbyMode}
                        className='text-slate-400 hover:text-slate-600 shrink-0'
                      >
                        <X className='w-4 h-4' />
                      </button>
                    </div>
                    <RadiusFilter value={radius} onChange={v => updateParams({ radius: String(v) })} />
                  </div>
                ) : (
                  <div className='space-y-2'>
                    <CityCombobox
                      value={cityInput}
                      onChange={setCityInput}
                      onSelect={handleCitySelect}
                      placeholder='Rechercher une ville…'
                    />
                    <button
                      type='button'
                      onClick={handleGeolocation}
                      disabled={geoLoading}
                      className='w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-100 hover:border-slate-300 transition-colors disabled:opacity-50'
                    >
                      {geoLoading ? <Loader2 className='w-4 h-4 animate-spin' /> : <Locate className='w-4 h-4' />}
                      {geoLoading ? 'Localisation…' : 'Près de moi'}
                    </button>
                    {geoError && <p className='text-xs text-red-500'>{geoError}</p>}
                  </div>
                )}
              </div>

              {/* Trier par — masqué en mode proximité (ordre distance) */}
              {!nearbyMode && (
                <div className='mb-6'>
                  <label className='block text-sm font-semibold text-slate-700 mb-2'>Trier par</label>
                  <div className='relative'>
                    <select
                      value={sortBy}
                      onChange={e => {
                        updateParams({ sortBy: e.target.value, page: undefined });
                      }}
                      className='w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 appearance-none pr-10'
                    >
                      <option value='date'>Date</option>
                      <option value='name'>Nom (A-Z)</option>
                      <option value='price'>Prix</option>
                      <option value='currentTeams'>Places prises</option>
                    </select>
                    <ChevronDown className='w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none' />
                  </div>
                </div>
              )}

              {/* Accessibilité */}
              <div className='mb-6'>
                <label className='block text-sm font-semibold text-slate-700 mb-2'>
                  <span className='flex items-center gap-1.5'>
                    <Shield className='w-3.5 h-3.5 text-purple-600' /> Accessibilité
                  </span>
                </label>
                <div className='relative'>
                  <select
                    value={restriction}
                    onChange={e =>
                      updateParams({
                        restriction: e.target.value === 'all' ? undefined : e.target.value,
                        page: undefined,
                      })
                    }
                    className='w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-700 appearance-none pr-10'
                  >
                    <option value='all'>Tous les concours</option>
                    <option value='open'>Ouvert au public</option>
                    <option value='licensed'>Réservé aux licenciés</option>
                    <option value='affiliated'>Réservé aux clubs affiliés</option>
                    <option value='both'>Licenciés + clubs affiliés</option>
                  </select>
                  <ChevronDown className='w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none' />
                </div>
              </div>

              {/* Statut */}
              <div className='mb-6'>
                <label className='block text-sm font-semibold text-slate-700 mb-3'>Statut</label>
                <div className='space-y-2'>
                  {/* Feature 5 (2026-08) — « Tous » lève le filtre de statut
                      (le défaut, sans ce choix, est « Inscriptions ouvertes »). */}
                  <label
                    className='flex items-center gap-3 cursor-pointer group'
                    onClick={() => updateParams({ status: ALL_STATUSES, page: undefined })}
                  >
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                        status === '' ? 'border-blue-500 bg-blue-500' : 'border-slate-300 group-hover:border-blue-400'
                      }`}
                    >
                      <Check className={`w-3 h-3 ${status === '' ? 'text-white' : 'text-transparent'}`} />
                    </div>
                    <span className={`text-sm ${status === '' ? 'text-blue-700 font-semibold' : 'text-slate-600'}`}>
                      Tous les statuts
                    </span>
                  </label>
                  {[
                    { label: 'Inscriptions ouvertes', value: CompetitionStatus.REGISTRATION_OPEN },
                    { label: 'Inscriptions fermées', value: CompetitionStatus.REGISTRATION_CLOSED },
                    { label: 'En cours', value: CompetitionStatus.IN_PROGRESS },
                  ].map(s => {
                    const isSelected = status === s.value;
                    return (
                      <label
                        key={s.value}
                        className='flex items-center gap-3 cursor-pointer group'
                        onClick={() => toggleStatus(s.value)}
                      >
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300 group-hover:border-blue-400'
                          }`}
                        >
                          <Check className={`w-3 h-3 ${isSelected ? 'text-white' : 'text-transparent'}`} />
                        </div>
                        <span className={`text-sm ${isSelected ? 'text-blue-700 font-semibold' : 'text-slate-600'}`}>
                          {s.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Type */}
              <div className='mb-6'>
                <label className='block text-sm font-semibold text-slate-700 mb-3'>Type</label>
                <div className='space-y-2'>
                  {[
                    { label: 'Tête-à-tête', value: CompetitionType.SINGLE },
                    { label: 'Doublette', value: CompetitionType.DOUBLE },
                    { label: 'Triplette', value: CompetitionType.TRIPLE },
                  ].map(t => {
                    const isSelected = type === t.value;
                    return (
                      <label
                        key={t.value}
                        className='flex items-center gap-3 cursor-pointer group'
                        onClick={() => toggleType(t.value)}
                      >
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300 group-hover:border-blue-400'
                          }`}
                        >
                          <Check className={`w-3 h-3 ${isSelected ? 'text-white' : 'text-transparent'}`} />
                        </div>
                        <span className={`text-sm ${isSelected ? 'text-blue-700 font-semibold' : 'text-slate-600'}`}>
                          {t.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Niveau */}
              <div className='mb-6'>
                <label className='block text-sm font-semibold text-slate-700 mb-3'>Niveau</label>
                <div className='space-y-2'>
                  {[
                    { label: 'National', value: CompetitionLevel.NATIONAL },
                    { label: 'Régional', value: CompetitionLevel.REGIONAL },
                    { label: 'Départemental', value: CompetitionLevel.DEPARTMENTAL },
                    { label: 'Local', value: CompetitionLevel.LOCAL },
                  ].map(n => {
                    const isSelected = level === n.value;
                    return (
                      <label
                        key={n.value}
                        className='flex items-center gap-3 cursor-pointer group'
                        onClick={() => toggleLevel(n.value)}
                      >
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300 group-hover:border-blue-400'
                          }`}
                        >
                          <Check className={`w-3 h-3 ${isSelected ? 'text-white' : 'text-transparent'}`} />
                        </div>
                        <span className={`text-sm ${isSelected ? 'text-blue-700 font-semibold' : 'text-slate-600'}`}>
                          {n.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={resetFilters}
                className='w-full bg-slate-100 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-200 transition-colors flex items-center justify-center gap-2'
              >
                <Filter className='w-4 h-4' /> Réinitialiser les filtres
              </button>
            </div>
          </aside>

          {/* Results */}
          <div className='flex-1'>
            <div className='flex items-center justify-between mb-6'>
              {nearbyMode ? (
                <p className='text-slate-500'>
                  <strong className='text-slate-900'>
                    {isNearbyLoading ? '…' : displayTotal.toLocaleString('fr-FR')}
                  </strong>{' '}
                  concours dans un rayon de <strong className='text-blue-600'>{radius} km</strong> autour de{' '}
                  <strong className='text-slate-700'>{cityLabel}</strong>
                </p>
              ) : (
                <p className='text-slate-500'>
                  <strong className='text-slate-900'>{isLoading ? '…' : total.toLocaleString('fr-FR')}</strong> concours
                  trouvés
                </p>
              )}
              {isFetching && !isLoading && !nearbyMode && (
                <span className='text-xs text-slate-400'>Actualisation…</span>
              )}
            </div>

            {/* Feature 6 (2026-08) — filtre actif « organisateur » (dismissible) */}
            {organizer && !nearbyMode && (
              <div className='mb-4'>
                <span className='inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1.5 text-sm font-medium text-purple-700'>
                  Concours d&apos;un organisateur sélectionné
                  <button
                    type='button'
                    onClick={() => updateParams({ organizer: undefined, page: undefined })}
                    className='text-purple-400 transition hover:text-purple-600'
                    aria-label='Retirer le filtre organisateur'
                  >
                    <X className='h-4 w-4' />
                  </button>
                </span>
              </div>
            )}

            {displayLoading ? (
              <div className='space-y-4'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='h-40 animate-pulse rounded-3xl bg-slate-200' />
                ))}
              </div>
            ) : nearbyMode ? (
              (nearbyData?.length ?? 0) === 0 ? (
                <div className='rounded-3xl border border-dashed border-slate-300 bg-white p-16 text-center'>
                  <MapPin className='w-12 h-12 text-slate-300 mx-auto mb-4' />
                  <p className='text-slate-500 font-medium'>Aucun concours dans ce rayon</p>
                  <p className='text-slate-400 text-sm mt-1'>Essayez d'élargir le rayon de recherche</p>
                </div>
              ) : (
                <div className='space-y-4'>
                  {(nearbyData ?? []).map(nc => (
                    <CompetitionCard
                      key={nc.id}
                      c={nc as unknown as Competition}
                      distanceKm={nc.distance_km}
                      isRegistered={registeredCompetitionIds.has(nc.id)}
                    />
                  ))}
                </div>
              )
            ) : competitions.length === 0 ? (
              <div className='rounded-3xl border border-dashed border-slate-300 bg-white p-16 text-center'>
                <Trophy className='w-12 h-12 text-slate-300 mx-auto mb-4' />
                <p className='text-slate-500 font-medium'>Aucun concours trouvé</p>
                <button onClick={resetFilters} className='mt-4 text-blue-600 hover:text-blue-700 text-sm font-semibold'>
                  Réinitialiser les filtres
                </button>
              </div>
            ) : (
              <div className='space-y-4'>
                {competitions.map(c => (
                  <CompetitionCard key={c.id} c={c} isRegistered={registeredCompetitionIds.has(c.id)} />
                ))}
              </div>
            )}

            {/* Pagination — masquée en mode proximité */}
            {!nearbyMode && totalPages > 1 && (
              <div className='flex items-center justify-center gap-2 mt-12'>
                <button
                  onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                  disabled={page === 1}
                  className='w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-600 transition-colors disabled:opacity-40'
                >
                  <ChevronLeft className='w-5 h-5' />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span key={`d-${i}`} className='px-2 text-slate-400'>
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => updateParams({ page: String(p) })}
                      className={`w-10 h-10 flex items-center justify-center rounded-xl font-semibold text-sm transition-colors ${
                        p === page
                          ? 'bg-blue-600 text-white'
                          : 'border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600'
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  onClick={() => updateParams({ page: String(Math.min(totalPages, page + 1)) })}
                  disabled={page >= totalPages}
                  className='w-10 h-10 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-600 transition-colors disabled:opacity-40'
                >
                  <ChevronRight className='w-5 h-5' />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='px-6 py-20'>
        <div className='max-w-4xl mx-auto'>
          <div className='bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-12 text-center text-white relative overflow-hidden'>
            <div className='absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2' />
            <div className='absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2' />
            <div className='relative z-10'>
              <Star className='w-12 h-12 text-yellow-300 mx-auto mb-4' />
              <h2 className='text-3xl md:text-4xl font-extrabold mb-4'>Vous organisez des concours ?</h2>
              <p className='text-blue-100 text-lg mb-8 max-w-xl mx-auto'>
                Créez votre concours en ligne, gérez les inscriptions automatiquement et gagnez du temps sur
                l&apos;organisation.
              </p>
              <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
                <Link
                  to={ROUTES.REGISTER}
                  className='bg-white text-blue-700 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-colors hover-lift flex items-center gap-2'
                >
                  Créer mon concours <ArrowRight className='w-5 h-5' />
                </Link>
                <Link
                  to={ROUTES.FONCTIONNALITES}
                  className='border-2 border-white/30 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/10 transition-colors'
                >
                  Découvrir les fonctionnalités
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
